import { LoadingOverlay, Select, Text } from '@mantine/core';
import { useState, useEffect, FC, useMemo } from 'react';
import { i18n } from '@lingui/core';
import { IconChevronDown } from '@tabler/icons';
import PanePaper from '../../../Molecules/PanePaper';
import { EventS } from '../../../Service/EventS';
import { CustomEventState } from '../../../Types/EventT';
import BookingState from '../../../Types/Enums/BookingState';
import { DataS } from '../../../Service/DataS';
import { useJwtClaims } from '../../../Service/Context/AuthContext';
import { LicenceS } from '../../../Service/LicenceS';

const RecommendationMappings = () => {
  const [eventStates, setEventStates] = useState<CustomEventState[]>([]);
  const [loading, setLoading] = useState(true);
  const { licence } = useJwtClaims();
  const hasSufficientLicence = useMemo(() => LicenceS.hasSuitableLicence('INTERACTIVE', licence), [licence]);

  useEffect(() => {
    fetchStates();
  }, []);

  const fetchStates = async () => {
    setLoading(true);
    const resJSON = await EventS.fetchEventStates();
    setEventStates(resJSON);
    setLoading(false);
  };

  return (
    <PanePaper title="Buchungsstatus für vermittelte Buchungen">
      <Text mb="md">
        Wenn du eine Buchung von einem anderen Dienstleister bzw. einer Agentur erhältst und die Agentur den Status z.B.
        auf "bestätigt" setzt, kannst du hier einstellen auf welchen Buchungsstatus umgestellt werden soll.
      </Text>
      <LoadingOverlay visible={loading} />
      {['OPEN', 'OFFERED', 'BOOKED', 'CANCELED'].map((bookingState) => (
        <StateSelection
          key={bookingState}
          states={eventStates}
          bookingState={bookingState as BookingState}
          hasSufficientLicence={hasSufficientLicence}
        />
      ))}
    </PanePaper>
  );
};

const StateSelection: FC<{ states: CustomEventState[]; bookingState: BookingState; hasSufficientLicence: boolean }> = ({
  states,
  bookingState,
  hasSufficientLicence,
}) => {
  if (states.length === 0) return null;

  const data = states.filter((s) => !s.dummy && s.originState === bookingState);
  const selected = data.find((s) => s.useForRecommendation);
  const updateRecommendationState = (id: string) => EventS.editStatus(id, { useForRecommendation: true });

  return (
    <Select
      mb="xs"
      disabled={data.length === 0 || !hasSufficientLicence}
      defaultValue={selected?.id}
      onChange={(value) => value && updateRecommendationState(value)}
      label={i18n._(`bookingState.${bookingState}`)}
      data={DataS.getEventStateOptionsV2(data)}
      sx={{ maxWidth: 400 }}
      styles={{ rightSection: { pointerEvents: 'none' } }}
      rightSection={<IconChevronDown size={14} />}
      rightSectionWidth={25}
      nothingFound="Keine Status"
    />
  );
};

export default RecommendationMappings;
